import React from 'react'
import styled from 'styled-components'
import {
  Box,
  Text,
  getPaletteColor,
  Flex,
  Badge,
  Hide,
  Link,
  Image
} from 'pcln-design-system'
import { CalendarCheck as CalendarCheckIcon } from 'pcln-icons'

import { HighlightedFlex, HotelsFill } from '@pcln/brand'
import { addDays, parseISO, format } from 'date-fns'
import analytics from '@/shared-utils/analytics'
import { ANALYTICS_CATEGORY_HOME } from '@/constants/analytics'
import getHotelLink from '@/shared-utils/get-hotel-link'
import generateThumbnailURL from './utils'
import LayoutComponentWrapper from '../LayoutComponentWrapper'

const FlexWithTopBorder = styled(Flex)`
  border-top: 4px solid ${getPaletteColor('primary.base')};
  height: 100%;
`
const CalendarIcon = styled(CalendarCheckIcon)`
  position: absolute;
`
const HotelsFillIcon = styled(HotelsFill)`
  line-height: 0;
`

type ExtendStayType = {
  travelEndDateTime: string
  cityName: string
  hotelID: string
  numRooms?: number
  title: string
  customerFirstName?: string
}

const renderExtendStayButton = (
  hotelID: string,
  endDate: number | Date,
  numRooms: number
) => {
  return (
    <Link
      href={getHotelLink(hotelID, endDate, addDays(endDate, 1), numRooms)}
      onClick={() => {
        analytics.fireEvent({
          action: 'extend_trip',
          category: ANALYTICS_CATEGORY_HOME
        })
      }}
      variation="fill"
      target="_blank"
    >
      Extend your Stay
    </Link>
  )
}

export default function ExtendStay({
  travelEndDateTime,
  cityName,
  hotelID,
  numRooms = 1,
  title,
  customerFirstName
}: ExtendStayType) {
  const endDate = parseISO(travelEndDateTime)
  const formattedEndDate = format(endDate, 'EEE, MMM dd')
  const image = generateThumbnailURL(hotelID)

  const trackViewTripClick = () => {
    analytics.fireEvent({
      action: 'Feature Box Link',
      category: ANALYTICS_CATEGORY_HOME,
      label: 'Cross Sell View Trip'
    })
  }
  const tripTitle = customerFirstName
    ? `${customerFirstName}, here are your trip details`
    : 'Here are your trip details'
  const sectionTitle = `${tripTitle},  Extend your hotel stay in ${cityName}`
  return (
    <LayoutComponentWrapper>
      <HighlightedFlex
        shadowOffsetX={24}
        shadowOffsetY={24}
        maxWidth="1216px"
        aria-label={sectionTitle}
        flexDirection={['column', null, 'row']}
      >
        <FlexWithTopBorder
          flexDirection="column"
          width={[1, null, null, 4 / 11, 3 / 11]}
          p={3}
          borderRadius="xl"
          boxShadowSize="sm"
          justifyContent="center"
          color="background.lightest"
        >
          <Text color="text.heading" textStyle="heading2" mb={3}>
            {tripTitle}
          </Text>
          <Flex mb={3} alignItems="center">
            <Image
              width="88px"
              borderRadius="xl"
              src={image}
              mr={3}
              alt={`Picture of ${title}, ${cityName}`}
            />
            <Box>
              <Text mb={1} textStyle="heading5">{`${title}, ${cityName}`}</Text>
              <Text textStyle="subheading6">{`Check-out on ${formattedEndDate}`}</Text>
            </Box>
          </Flex>

          <Link
            href="/next-profile/trips"
            onClick={trackViewTripClick}
            variation="fill"
          >
            View Trip
          </Link>
        </FlexWithTopBorder>

        <Flex mt="24px" py={2} width={[1, null, null, 7 / 11, 8 / 11]}>
          <Flex flexDirection="column" justifyContent="center">
            <Hide xs sm md lg>
              <Flex
                bg="background.lightest"
                borderRadius="full"
                color="success.dark"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                p="24px"
                m={4}
              >
                <HotelsFillIcon size={5} />
              </Flex>
            </Hide>
          </Flex>
          <Flex
            flexDirection="column"
            justifyContent="center"
            ml={[3, null, null, null, 0]}
            mr={[3, null, null, 1, 5]}
          >
            <Box mb={2}>
              <Badge bg="highlight.light" color="highlight.shade">
                <CalendarIcon size="14px" mr={1} />
                <Box pl={3}>STAY LONGER</Box>
              </Badge>
            </Box>
            <Text
              color="text.heading"
              mb={3}
              textStyle={['heading4', null, null, 'heading3']}
            >
              Extend your hotel stay in {cityName} & save even more with
              Priceline’s best available rates.*
            </Text>
            <Box mb={4}>
              {renderExtendStayButton(hotelID, endDate, numRooms)}
            </Box>
            <Text color="text.base" textStyle="disclaimer">
              * This will be a separate reservation. Your current reservation
              will remain unchanged and in rare cases, the hotel may require you
              to move rooms.
            </Text>
          </Flex>
        </Flex>
      </HighlightedFlex>
    </LayoutComponentWrapper>
  )
}
